import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { PassoProduto, ProdutoCatalogo } from "@/api"
import { chaveAdicional, montarEtapas, montarPassos, nomesDosAdicionais, selecoesIniciais, type EtapaPasso, type PassoSelecionado, type SelecoesPassos } from "../passos"

const DELAY_AUTO_AVANCO = 400

/** Adicionais que saíram junto com a opção desmarcada, para avisar sob o passo dela até a próxima mudança. */
export interface AdicionaisRemovidos {
    chave: string
    sabor: string
    adicionais: string[]
}

export interface StepWizard {
    etapas: EtapaPasso[]
    indiceAtivo: number
    etapaAtual?: EtapaPasso
    selecoes: SelecoesPassos
    basePrice: number
    finalPrice: number
    todosValidos: boolean
    concluido: boolean
    adicionaisRemovidos: AdicionaisRemovidos | null
    quantidadePasso: (chave: string) => number
    passoValido: (etapa: EtapaPasso) => boolean
    podeAcessar: (indice: number) => boolean
    irPara: (indice: number) => void
    avancar: () => void
    voltar: () => void
    alterarOpcao: (etapa: EtapaPasso, itemId: number, delta: number) => void
}

interface UseStepWizardParams {
    produto: ProdutoCatalogo | null
    /** Passos do item em edição — `null` num item novo. */
    passosSalvos: PassoSelecionado[] | null
    aberto: boolean
    adicionalPorOpcao: boolean
}

export function useStepWizard({ produto, passosSalvos, aberto, adicionalPorOpcao }: UseStepWizardParams): StepWizard {
    const passos = useMemo<PassoProduto[]>(
        () => (produto ? [...produto.passos].sort((a, b) => a.ordem - b.ordem) : []),
        [produto],
    )

    const [posicao, setPosicao] = useState(0)
    const [selecoes, setSelecoes] = useState<SelecoesPassos>({})
    const [adicionaisRemovidos, setAdicionaisRemovidos] = useState<AdicionaisRemovidos | null>(null)
    const timerAvanco = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    const etapas = useMemo(
        () => montarEtapas(passos, selecoes, adicionalPorOpcao),
        [passos, selecoes, adicionalPorOpcao],
    )
    const indiceAtivo = Math.min(posicao, etapas.length)

    const limparTimer = useCallback(() => {
        if (timerAvanco.current) clearTimeout(timerAvanco.current)
    }, [])

    // Sair do passo tira o aviso: voltar a ele depois não deve mostrar uma troca antiga.
    const sairDoPasso = useCallback(() => {
        limparTimer()
        setAdicionaisRemovidos(null)
    }, [limparTimer])

    useEffect(() => {
        if (!aberto || !produto) return

        setSelecoes(selecoesIniciais(passos, passosSalvos))
        setAdicionaisRemovidos(null)
        setPosicao(0)
    }, [aberto, produto, passos, passosSalvos])

    useEffect(() => {
        if (!aberto) limparTimer()
        return limparTimer
    }, [aberto, limparTimer])

    const quantidadePasso = useCallback(
        (chave: string) => Object.values(selecoes[chave] ?? {}).reduce((s, q) => s + q, 0),
        [selecoes],
    )

    const passoValido = useCallback(
        ({ chave, passo }: EtapaPasso): boolean => {
            const total = quantidadePasso(chave)
            return total >= passo.quantidadeMinima && total <= passo.quantidadeMaxima
        },
        [quantidadePasso],
    )

    const podeAcessar = useCallback(
        (indice: number): boolean => etapas.slice(0, indice).every(passoValido),
        [etapas, passoValido],
    )

    const irPara = useCallback(
        (indice: number): void => {
            sairDoPasso()
            if (indice >= 0 && indice <= etapas.length && podeAcessar(indice)) setPosicao(indice)
        },
        [sairDoPasso, etapas.length, podeAcessar],
    )

    const avancar = useCallback((): void => {
        sairDoPasso()
        setPosicao((i) => Math.min(i + 1, etapas.length))
    }, [sairDoPasso, etapas.length])

    const voltar = useCallback((): void => {
        sairDoPasso()
        setPosicao((i) => Math.max(Math.min(i, etapas.length) - 1, 0))
    }, [sairDoPasso, etapas.length])

    const alterarOpcao = useCallback(
        ({ chave, passo }: EtapaPasso, itemId: number, delta: number): void => {
            const desmarcou = delta < 0 && (selecoes[chave]?.[itemId] ?? 0) + delta <= 0
            const removidos = desmarcou ? nomesDosAdicionais(passo, selecoes, itemId) : []
            const sabor = passo.tabelaPreco?.itens.find((it) => it.id === itemId)?.nomeProduto ?? ''

            setAdicionaisRemovidos(removidos.length > 0 ? { chave, sabor, adicionais: removidos } : null)

            setSelecoes((prev) => {
                const selecao = { ...(prev[chave] ?? {}) }
                const totalPasso = Object.values(selecao).reduce((s, q) => s + q, 0)

                if (delta > 0 && totalPasso >= passo.quantidadeMaxima) return prev

                const proximo = (selecao[itemId] ?? 0) + delta
                if (proximo <= 0) delete selecao[itemId]
                else selecao[itemId] = proximo

                const atualizadas = { ...prev, [chave]: selecao }

                // Sabor desmarcado leva os adicionais junto: remarcá-lo depois começa sem nenhum.
                if (proximo <= 0 && passo.subPasso) delete atualizadas[chaveAdicional(passo.id, passo.subPasso.id, itemId)]

                return atualizadas
            })

            const atingiuMaximo = quantidadePasso(chave) + delta >= passo.quantidadeMaxima

            // A etapa seguinte pode nascer desta escolha (adicionais do sabor): o limite sai do índice clampado no render.
            if (delta > 0 && atingiuMaximo) {
                limparTimer()
                timerAvanco.current = setTimeout(() => setPosicao((i) => i + 1), DELAY_AUTO_AVANCO)
            }
        },
        [selecoes, quantidadePasso, limparTimer],
    )

    const basePrice = produto?.valor ?? 0
    const extras = useMemo(
        () => (produto ? montarPassos(produto, selecoes).reduce((s, p) => s + p.valorPasso, 0) : 0),
        [produto, selecoes],
    )

    return {
        etapas,
        indiceAtivo,
        etapaAtual: etapas[indiceAtivo],
        selecoes,
        basePrice,
        finalPrice: basePrice + extras,
        todosValidos: etapas.every(passoValido),
        concluido: etapas.length > 0 && indiceAtivo >= etapas.length,
        adicionaisRemovidos,
        quantidadePasso,
        passoValido,
        podeAcessar,
        irPara,
        avancar,
        voltar,
        alterarOpcao,
    }
}
